import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useCustomer, useSession } from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import { Button } from '../Button'
import { CreateCustomerSavedPaymentMethodCardDialogue } from '../CreateCustomerSavedPaymentMethodCardDialogue'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './AuthenticationDrawer.module.scss'
import { AuthenticationDrawerContentProps } from './AuthenticationDrawer.types'

const PAYMENT_METHOD_ADYEN_CARD = 'adyen-card'

const SignUpAuthenticationForm = dynamic(
  async () => {
    const mod = await import('./SignUpAuthenticationForm')
    return mod.SignUpAuthenticationForm
  },
  { ssr: false }
)

const CreateCustomerSavedAddressDialogue = dynamic(
  async () => {
    const mod = await import('../CreateCustomerSavedAddressDialogue')
    return mod.CreateCustomerSavedAddressDialogue
  },
  { ssr: false }
)

export const SignUpAuthenticationDrawer = ({
  onClose,
  enabledPaymentMethods,
}: AuthenticationDrawerContentProps) => {
  const [isSignedUpSuccessful, setIsSignedUpSuccessful] =
    useState<boolean>(false)
  const [
    isCustomerSavedAddressDialogueOpen,
    setIsCustomerSavedAddressDialogueOpen,
  ] = useState(false)
  const [
    isCustomerSavedPaymentMethodCardDialogue,
    setIsCustomerSavedPaymentMethodCardDialogue,
  ] = useState(false)
  const { addPaymentMethods, customer, defaultCustomerAddress } = useCustomer()
  const { session } = useSession()

  const canAddNewCard = useMemo(() => {
    return enabledPaymentMethods?.includes(PAYMENT_METHOD_ADYEN_CARD)
  }, [enabledPaymentMethods])

  useEffect(() => {
    isSignedUpSuccessful &&
      session?.token?.isLoggedIn &&
      customer?.email &&
      GTM.dispatch(GTMEvent.USER_IDENTIFY, {
        customer,
        defaultCustomerAddress,
      })
  }, [
    customer,
    defaultCustomerAddress,
    isSignedUpSuccessful,
    session?.token?.isLoggedIn,
  ])

  if (!isSignedUpSuccessful) {
    return (
      <SignUpAuthenticationForm
        setIsSignedUpSuccessfull={setIsSignedUpSuccessful}
      />
    )
  }

  return (
    <>
      <div className='px-4 py-6 flex flex-col gap-4'>
        <Typography
          variant={TypographyVariant.Heading5}
          tag={TypographyTag.p}
          className={classNames('hidden', styles.jaycarContent)}>
          Welcome to Jaycar Electronics
        </Typography>
        <Typography
          variant={TypographyVariant.Heading5}
          tag={TypographyTag.p}
          className={classNames('hidden', styles.rtmContent)}>
          Welcome to Road Tech Marine Electronics
        </Typography>
        <Typography
          tag={TypographyTag.div}
          variant={TypographyVariant.BodyRegularExtraLineHeight}>
          Thank you for Registering with
          <span
            className={classNames('hidden ml-1', styles.jaycarContentInline)}>
            Jaycar
          </span>
          <span className={classNames('hidden ml-1', styles.rtmContentInline)}>
            Road Tech Marine
          </span>
          , we hope you will enjoy shopping with us.
        </Typography>
      </div>
      <div className='border' />
      <div className='px-4 py-9 md:p-9 flex flex-col gap-4 flex-1'>
        <Typography
          tag={TypographyTag.div}
          variant={TypographyVariant.BodyRegularExtraLineHeight}>
          Finish your account setup and save time when shopping online.
        </Typography>
        <div className='flex gap-2 md:gap-4 items-center'>
          <Button
            fluid
            status='secondary'
            theme={{
              root: 'p-0',
            }}
            disabled={!canAddNewCard}
            onClick={() => {
              setIsCustomerSavedPaymentMethodCardDialogue(true)
            }}>
            Add payment method
          </Button>
          <Button
            status='primary'
            fluid
            theme={{
              root: 'p-0',
            }}
            onClick={() => {
              setIsCustomerSavedAddressDialogueOpen(true)
            }}>
            Add a delivery address
          </Button>
        </div>
        <Typography
          variant={TypographyVariant.BodyRegularExtraLineHeight}
          tag={TypographyTag.p}>
          For any enquiries, visit our{' '}
          <Link href='/help' className='underline'>
            help center
          </Link>{' '}
          or{' '}
          <Link href='/contactus' className='underline'>
            contact us
          </Link>{' '}
          for more information
        </Typography>
      </div>

      <CreateCustomerSavedAddressDialogue
        isOpen={isCustomerSavedAddressDialogueOpen}
        onClose={() => {
          return setIsCustomerSavedAddressDialogueOpen(false)
        }}
      />
      <CreateCustomerSavedPaymentMethodCardDialogue
        isOpen={isCustomerSavedPaymentMethodCardDialogue}
        onConfirm={addPaymentMethods}
        onCancel={() => {
          return setIsCustomerSavedPaymentMethodCardDialogue(false)
        }}
        onOk={() => {
          return setIsCustomerSavedPaymentMethodCardDialogue(false)
        }}
      />
      <div
        className={classNames(
          styles.footerBottom,
          'flex items-center gap-x-4 w-full border-t'
        )}>
        <Button
          status='primary'
          fluid
          size='lg'
          theme={{ root: styles.footerButton }}
          onClick={() => {
            onClose(true)
          }}>
          Continue to Jaycar
        </Button>
      </div>
    </>
  )
}
